import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageShell from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import PasswordInput from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { corporateInput, EmptyState } from "@/components/Shared";
import { Plus, Trash, ShieldCheck, EyeSlash, PencilSimple } from "@phosphor-icons/react";
import RowMenu from "@/components/RowMenu";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";

export default function AdminUsersPage() {
  const { lang } = useI18n();
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "customer_service" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const startCreate = () => {
    setEditing(null);
    setForm({ name: "", email: "", password: "", role: "customer_service" });
    setOpen(true);
  };

  const startEdit = (u) => {
    setEditing(u);
    setForm({ name: u.name, email: u.email, password: "", role: u.role });
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        const body = { name: form.name, role: form.role };
        if (form.password) body.password = form.password;
        await api.patch(`/admin/users/${editing.id}`, body);
        toast.success(lang === "en" ? "User updated" : "Usuario actualizado");
      } else {
        await api.post("/admin/users", form);
        toast.success(lang === "en" ? "User created" : "Usuario creado");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const remove = async (u) => {
    if (!confirm(lang === "en" ? `Delete ${u.name}?` : `¿Eliminar a ${u.name}?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success(lang === "en" ? "Deleted" : "Eliminado");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const ROLE_LABEL = {
    super_admin: lang === "en" ? "Super admin" : "Super admin",
    admin: lang === "en" ? "Accountant" : "Contable",
    customer_service: lang === "en" ? "Customer Service" : "Servicio al Cliente",
    limited_admin: lang === "en" ? "Read-only" : "Solo lectura",
  };

  const ROLE_HELP = {
    admin: lang === "en"
      ? "Handles finance: invoices, accounting, reports, payouts, agents, plans. Cannot send new connections."
      : "Gestiona finanzas: facturas, contabilidad, reportes, payouts, agentes, planes. No envía nuevas conexiones.",
    customer_service: lang === "en"
      ? "Handles clients: onboarding, disconnections, calendar, communications. Can see amounts to confirm payments. No accounting."
      : "Atiende clientes: alta, desconexiones, calendario, comunicaciones. Ve montos para confirmar pagos. Sin contabilidad.",
    limited_admin: lang === "en" ? "Read-only across everything." : "Solo lectura general.",
  };

  return (
    <PageShell
      section={lang === "en" ? "System" : "Sistema"}
      page={lang === "en" ? "Admin users" : "Usuarios administradores"}
      context={`${users.length} ${lang === "en" ? "users" : "usuarios"}`}
      testid="admin-users-toolbar"
      actions={
        <Button onClick={startCreate} className="bg-slate-900 hover:bg-slate-800 text-white h-9" data-testid="add-admin">
          <Plus size={15} className="mr-1.5" /> {lang === "en" ? "New admin" : "Nuevo admin"}
        </Button>
      }
    >
      <div className="mb-6 border-l-2 border-amber-400 pl-4 py-2">
        <div className="text-sm font-semibold text-slate-900 dark:text-white">
          {lang === "en" ? "Manage admin users" : "Gestiona usuarios del panel administrativo"}
        </div>
        <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 leading-relaxed">
          {lang === "en" ? (
            <>
              <strong>Super admin</strong> sees everything. <strong>Accountant</strong> handles finance (no new-connection sending). <strong>Customer Service</strong> handles clients & operations, sees amounts to confirm payments, no accounting access.
            </>
          ) : (
            <>
              <strong>Super admin</strong> ve todo. <strong>Contable</strong> gestiona finanzas (no envía nuevas conexiones). <strong>Servicio al Cliente</strong> gestiona clientes y operaciones, ve montos para confirmar pagos, sin acceso a contabilidad.
            </>
          )}
        </div>
      </div>

      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800/40 border-b border-slate-200 dark:border-slate-800">
            <tr className="text-left">
              <th className="px-5 py-3 text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500 dark:text-slate-400">{lang === "en" ? "Name" : "Nombre"}</th>
              <th className="px-5 py-3 text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500 dark:text-slate-400">Email</th>
              <th className="px-5 py-3 text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500 dark:text-slate-400">{lang === "en" ? "Role" : "Rol"}</th>
              <th className="px-5 py-3 text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500 dark:text-slate-400">{lang === "en" ? "Created" : "Creado"}</th>
              <th className="px-5 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {!loading && users.length === 0 && (
              <tr><td colSpan={5}>
                <EmptyState icon={ShieldCheck} title={lang === "en" ? "No admins yet" : "Sin admins"} message={lang === "en" ? "Create your first admin" : "Crea tu primer admin"} />
              </td></tr>
            )}
            {users.map((u) => {
              const isMe = u.email === me?.email;
              const isLimited = u.role === "limited_admin";
              return (
                <tr key={u.id} data-testid={`admin-row-${u.id}`} className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                  <td className="px-5 py-4 font-medium text-slate-900 dark:text-slate-100">
                    {u.name}
                    {isMe && <span className="ml-2 text-[10px] uppercase tracking-wider font-semibold text-amber-600 dark:text-amber-400">· {lang === "en" ? "you" : "tú"}</span>}
                  </td>
                  <td className="px-5 py-4 text-slate-600 dark:text-slate-400 font-mono text-xs">{u.email}</td>
                  <td className="px-5 py-4">
                    <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded border ${
                      u.role === "super_admin"
                        ? "border-red-300 text-red-700 bg-red-50 dark:bg-red-950/30 dark:border-red-800 dark:text-red-300"
                        : u.role === "admin"
                          ? "border-emerald-300 text-emerald-700 bg-emerald-50 dark:bg-emerald-950/30 dark:border-emerald-800 dark:text-emerald-300"
                          : u.role === "customer_service"
                            ? "border-blue-300 text-blue-700 bg-blue-50 dark:bg-blue-950/30 dark:border-blue-800 dark:text-blue-300"
                            : "border-amber-300 text-amber-700 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 dark:text-amber-300"
                    }`}>
                      {isLimited && <EyeSlash size={11} />}
                      {ROLE_LABEL[u.role] || u.role}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-slate-600 dark:text-slate-400 font-mono text-xs">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-5 py-4 text-right whitespace-nowrap">
                    {u.role !== "super_admin" && !isMe && (
                      <RowMenu
                        testid={`admin-menu-${u.id}`}
                        items={[
                          { label: lang === "en" ? "Edit" : "Editar", icon: PencilSimple, onClick: () => startEdit(u), testid: `edit-admin-${u.id}` },
                          { type: "separator" },
                          { label: lang === "en" ? "Delete" : "Eliminar", icon: Trash, onClick: () => remove(u), testid: `del-admin-${u.id}`, danger: true },
                        ]}
                      />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? (lang === "en" ? "Edit admin" : "Editar admin") : (lang === "en" ? "New admin" : "Nuevo admin")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">{lang === "en" ? "Name" : "Nombre"} *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={corporateInput} data-testid="admin-name" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Email *</Label>
              <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={!!editing} className={corporateInput} data-testid="admin-email" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                {editing ? (lang === "en" ? "New password (optional)" : "Nueva contraseña (opcional)") : (lang === "en" ? "Password" : "Contraseña") + " *"}
              </Label>
              <PasswordInput value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={editing ? (lang === "en" ? "leave blank to keep" : "vacío = mantener") : ""} className={corporateInput} data-testid="admin-password" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">{lang === "en" ? "Role" : "Rol"} *</Label>
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={corporateInput} data-testid="admin-role">
                <option value="customer_service">{ROLE_LABEL.customer_service}</option>
                <option value="admin">{ROLE_LABEL.admin}</option>
                <option value="limited_admin">{ROLE_LABEL.limited_admin}</option>
              </select>
              <p className="text-[10px] text-slate-500 mt-1 leading-snug">
                {ROLE_HELP[form.role] || ""}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>{lang === "en" ? "Cancel" : "Cancelar"}</Button>
            <Button onClick={save} disabled={saving || !form.name || !form.email || (!editing && !form.password)} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="save-admin">
              {saving ? (lang === "en" ? "Saving..." : "Guardando...") : (lang === "en" ? "Save" : "Guardar")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}
